/**
 * Onboarding copy (F-NAV-17 … F-NAV-22), verbatim from the parity spec except
 * where §9.2 forbids the native sentence from being true on the web.
 *
 * Three substitutions, all on page 3, and each of them is a promise the browser
 * cannot keep rather than a wording preference:
 *
 * - **Haptics.** Unsupported in Safari and on iOS, so §9.2 replaces them with a
 *   synchronized visual pulse — which `@ff/domain`'s `WEB_CUE_OUTPUT_CHANNELS`
 *   and `CUE_VISUAL_PULSE_IS_ALWAYS_ON` already encode. Naming a channel the
 *   product does not have would make the redundancy arithmetic a lie in the one
 *   screen that teaches it.
 * - **The lock-screen cue section.** `LOCK_SCREEN_CUES_AVAILABLE` is `false` and
 *   `SCREEN_LOCKED_PANEL_COPY` is the reviewed replacement, so the section uses
 *   that instead of asking for a notification permission the app would then
 *   never schedule against (F-NAV-20 has no web counterpart at all).
 * - **The device noun.** `DEVICE_NOUN` is "this device"; the native string
 *   interpolated "iPhone"/"iPad" and reads ungrammatically with the neutral
 *   noun, so the possessive form is used.
 */

import { DEVICE_NOUN, SCREEN_LOCKED_PANEL_COPY } from "@ff/domain";

export interface OnboardingRow {
  readonly title: string;
  readonly detail: string;
}

export interface PunchCall {
  readonly number: number;
  readonly name: string;
  readonly side: string;
}

export interface TimelineMoment {
  readonly moment: string;
  readonly channel: string;
}

export const ONBOARDING_PAGE_COUNT = 4;

/** F-NAV-22: the indicator label is "{ShortTitle}, page {n} of 4". */
export const ONBOARDING_SHORT_TITLES: readonly string[] = ["Format", "Calls", "Cues", "Safety"];

export function pageIndicatorLabel(index: number): string {
  const shortTitle = ONBOARDING_SHORT_TITLES[index] ?? "";
  return `${shortTitle}, page ${index + 1} of ${ONBOARDING_PAGE_COUNT}`;
}

// --- Page 1 — Format (F-NAV-17) --------------------------------------------

export const FORMAT_PAGE = {
  eyebrow: "01 / Your workout",
  title: "Time the work. Add guidance when you want it.",
  detail:
    "Choose a simple round clock or build a guided session with drills, combinations, and transitions.",
  rows: [
    {
      title: "Simple timer",
      detail: "Set work, rest, rounds, and the time between exercises.",
    },
    {
      title: "Guided workout",
      detail:
        "Arrange bag work, jump rope, defense, shadowboxing, and recovery in your own order.",
    },
    {
      title: "Change it anytime",
      detail: "Reorder drills, tune intervals, or repeat a session from your history.",
    },
  ] as readonly OnboardingRow[],
} as const;

// --- Page 2 — Calls (F-NAV-18) ---------------------------------------------

export const CALLS_PAGE = {
  eyebrow: "02 / Boxing calls",
  title: "Know the numbers. Keep your eyes up.",
  detail:
    "Calls use lead and rear sides, so the same language works from orthodox or southpaw stance.",
  punchMap: [
    { number: 1, name: "Jab", side: "Lead straight" },
    { number: 2, name: "Cross", side: "Rear straight" },
    { number: 3, name: "Lead hook", side: "Lead side" },
    { number: 4, name: "Rear hook", side: "Rear side" },
    { number: 5, name: "Lead uppercut", side: "Lead side" },
    { number: 6, name: "Rear uppercut", side: "Rear side" },
  ] as readonly PunchCall[],
  power: [
    { title: "P1 Touch", detail: "Fast, relaxed, and precise." },
    { title: "P2 Sharp", detail: "Intentional speed with controlled force." },
    { title: "P3 Power", detail: "Strong and technically sound—not reckless." },
    {
      title: "↙ ↘ Slip / roll",
      detail:
        "Move your head off line or under the returning punch, then reset your stance.",
    },
  ] as readonly OnboardingRow[],
} as const;

/** F-NAV-18: a punch row is one accessibility element, not three. */
export function punchCallLabel(call: PunchCall): string {
  return `Punch ${call.number}, ${call.name}, ${call.side}`;
}

// --- Page 3 — Cues (F-NAV-19, with the §9.2 substitutions) ------------------

export const CUES_PAGE = {
  eyebrow: "03 / Eyes-up cues",
  title: "The round should reach you—not the other way around.",
  detail:
    "Use bells, spoken calls, and a synchronized visual pulse while you train. Every critical state remains visible onscreen too.",
  timeline: [
    { moment: "Start", channel: "Bell" },
    { moment: "Halfway", channel: "Voice" },
    { moment: "Final 10", channel: "Pulse" },
    { moment: "Change", channel: "Double" },
  ] as readonly TimelineMoment[],
  backgroundTitle: SCREEN_LOCKED_PANEL_COPY.title,
  backgroundBody: SCREEN_LOCKED_PANEL_COPY.body,
  footnote: `Keep ${DEVICE_NOUN}'s volume audible if you rely on sound. The visual pulse and the screen provide additional signals, but you should confirm your cue setup before beginning.`,
} as const;

// --- Page 4 — Safety (F-NAV-21) --------------------------------------------

export const SAFETY_PAGE = {
  eyebrow: "04 / Train well",
  title: "Good rounds start with good judgment.",
  detail:
    "Fighting Form provides timing and optional guidance prompts. It does not assess your health, form, surroundings, or readiness to exercise.",
  rows: [
    {
      title: "01 Prepare",
      detail:
        "Warm up, clear your space, secure the bag, and use properly fitted wraps and gloves.",
    },
    {
      title: "02 Control",
      detail: "Choose technique before speed and speed before power. Keep every punch balanced.",
    },
    {
      title: "03 Listen",
      detail:
        "Pause or stop for pain, dizziness, unusual shortness of breath, numbness, or loss of control.",
    },
    {
      title: "04 Ask",
      detail:
        "If exercise may not be appropriate for you, get guidance from a qualified clinician or coach first.",
    },
  ] as readonly OnboardingRow[],
  closing:
    "You are always in charge of the round. Pause, skip, or end a session whenever you need to.",
} as const;

// --- Footer (F-NAV-22) ------------------------------------------------------

export const ONBOARDING_FOOTER = {
  back: "Back",
  skip: "Skip",
  advance: "Continue",
  finish: "Begin training",
} as const;

export const ONBOARDING_TITLE = "Getting started" as const;
